import { Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postAssetUpdate } from 'src/api/stoApi';
import { AssetEnergyDemand } from 'src/components/assets/addedit-forms/EnergyDemand';
import { AssetFloorplanDescription } from 'src/components/assets/addedit-forms/FloorplanDescription';
import { AssetGeneralInfo } from 'src/components/assets/addedit-forms/GeneralInfo';
import { AssetLocationDetails } from 'src/components/assets/addedit-forms/LocationDetails';
import { AssetPropertyOverview } from 'src/components/assets/addedit-forms/PropertyOverview';
import { AssetPropertyPhotos } from 'src/components/assets/addedit-forms/PropertyPhotos';
import { AssetRentalIncome } from 'src/components/assets/addedit-forms/RentalIncome';
import { AssetUploadPropertyDocuments } from 'src/components/assets/addedit-forms/UploadPropertyDocuments';
import { AssetDetailsLinkList } from 'src/components/assets/AssetDetailsLinkList';
import { SiteHeader } from 'src/components/ui/SiteHeader';
import { TwoColumnGridContainer } from 'src/components/ui/TwoColumnGridContainer';
import { TAssetStage } from '@brickwise/asset-validation/dist/schema/enums';
import { Routes } from 'src/router/Routes';
import { generalStore } from 'src/stores/GeneralStore';
import { isNullUndefinedOrEmpty, removeNullUndefined } from 'src/util/utils';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { Alert, Button, Snackbar } from '@mui/material';
import { useAsset } from '../../context-providers/AssetContext';
import { withZodSchema } from 'formik-validator-zod';
import { AssetFormError } from 'src/components/assets/form-components/AssetFormError';
import { CopyBrickwiseLinkButton } from 'src/components/assets/form-components/CopyBrickwiseLinkButton';
import { AssetFormActionsContainer } from 'src/components/assets/form-components/AssetFormActionsContainer';
import { POST_WITH_ID, upsertAssetSchema } from '@brickwise/asset-validation/dist/asset/assetData';
import { PowerplantOverview } from 'src/components/assets/powerplant/PowerplantOverview';
import { PowerplantDetails } from 'src/components/assets/powerplant/PowerplantDetails';
import { PlotOverview } from 'src/components/assets/plot/PlotOverview';
import { AssetTypeConditional } from 'src/components/user/AssetTypeConditional';
import { getInitFormValues } from 'src/util/formUtils';

export const EditAsset = () => {
  const { asset } = useAsset();
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  if (!asset) {
    return <>Asset Not Found!</>;
  }

  const { t } = useTranslation();

  const initialErrors = withZodSchema(upsertAssetSchema)(asset);

  const preprocessValues = async (values: POST_WITH_ID): Promise<POST_WITH_ID> => {
    const newAsset = removeNullUndefined(upsertAssetSchema.parse(values));
    if (newAsset.type !== 'citizenParticipationLoan') {
      delete newAsset.rentalDetails?.rentalReturn;
    }
    return newAsset;
  };

  const saveClicked = async (newAsset: POST_WITH_ID) => {
    // put asset
    generalStore.setIsLoading(true);
    try {
      await postAssetUpdate(asset?.id ?? '', newAsset);
      setOpenSuccessAlert(true);
    } catch (error: any) {
      generalStore.setError('Error while update asset');
    } finally {
      generalStore.setIsLoading(false);
    }
  };

  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.ASSETS.ROOT,
            label: t('dashboard.breadcrumbs.assets'),
            icon: <ApartmentOutlinedIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
          {
            link: Routes.ASSETS.EDIT.replace(':assetId', asset.id),
            label: isNullUndefinedOrEmpty(asset.descriptions?.title) ? asset.id : (asset.descriptions?.title as string),
          },
        ]}
        headline={isNullUndefinedOrEmpty(asset.descriptions?.title) ? asset.id : (asset.descriptions?.title as string)}
      >
        <AssetDetailsLinkList
          assetId={asset.id}
          useSerialNumber={asset?.useSerialNumber}
          active={'asset'}
          isPrimary={asset?.investmentDetails.stage === TAssetStage.primary}
          isSecondary={asset?.investmentDetails.stage === TAssetStage.secondary}
          isPreorder={asset?.investmentDetails.stage === TAssetStage.preorder}
          securityType={asset?.investmentDetails?.securityType}
        />
      </SiteHeader>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openSuccessAlert}
        autoHideDuration={6000}
        onClose={() => setOpenSuccessAlert(false)}
      >
        <Alert onClose={() => setOpenSuccessAlert(false)} severity="success" sx={{ width: '100%' }}>
          {t('dashboard.assets.addedit.successalert')}
        </Alert>
      </Snackbar>
      <Formik
        initialValues={getInitFormValues(upsertAssetSchema, asset)}
        initialErrors={initialErrors}
        initialTouched={initialErrors}
        validate={(values) => {
          const result = withZodSchema(upsertAssetSchema)(values);
          // We log this so we can debug stuff on the production as well.
          return result;
        }}
        onSubmit={async (values) => {
          const parseResult = upsertAssetSchema.safeParse(values);
          if (parseResult.success === true) {
            const preprocessedValues = await preprocessValues(parseResult.data);
            await saveClicked(preprocessedValues);
          }
          return;
        }}
      >
        {(props) => {
          return (
            <form onSubmit={props.handleSubmit}>
              <AssetGeneralInfo disabled={false} />
              <AssetTypeConditional supported={['powerplant', 'powerplantPortfolio']}>
                <TwoColumnGridContainer>
                  <PowerplantOverview disabled={false} />
                  <PowerplantDetails disabled={false} />
                </TwoColumnGridContainer>
              </AssetTypeConditional>

              <AssetTypeConditional supported={['realEstate', 'builderOwnerModel']}>
                <TwoColumnGridContainer>
                  <AssetPropertyOverview disabled={false} />
                  <AssetEnergyDemand disabled={false} />
                </TwoColumnGridContainer>
              </AssetTypeConditional>

              <AssetTypeConditional supported="plot">
                <PlotOverview disabled={false} />
              </AssetTypeConditional>

              <AssetTypeConditional supported="realEstate">
                <AssetFloorplanDescription disabled={false} />
              </AssetTypeConditional>
              <AssetRentalIncome disabled={false} />
              <AssetTypeConditional supported={['realEstate', 'builderOwnerModel', 'powerplant', 'plot']}>
                <AssetLocationDetails disabled={false} />
              </AssetTypeConditional>

              <AssetUploadPropertyDocuments
                disabled={asset.investmentDetails.stage === TAssetStage.primary || asset.investmentDetails.stage === TAssetStage.secondary}
              />
              <AssetPropertyPhotos disabled={false} />

              <AssetFormError />

              <AssetFormActionsContainer>
                <CopyBrickwiseLinkButton />

                <Button variant="contained" color="primary" disableElevation type="submit">
                  {t('dashboard.assets.addedit.main.savechanges')}
                </Button>
              </AssetFormActionsContainer>
            </form>
          );
        }}
      </Formik>
    </>
  );
};
